// Toast store — non-blocking status / error messages stacked top-right.
//
// Drop-in replacement for window.alert("X"). Each toast auto-dismisses
// after `durationMs` (default 4s) and can be closed early via the X.
//
// Usage:
//   import { toast } from "@/stores/toastStore";
//   toast.success("Saved");
//   toast.error("Save failed: " + e.message);
//   toast.info("Heads up...");
//
// Renders via <ToastViewport /> mounted once in AppLayout.

import { create } from "zustand";

export type ToastVariant = "success" | "error" | "info";

export interface Toast {
  id: number;
  message: string;
  variant: ToastVariant;
  durationMs: number;
}

interface ToastState {
  items: Toast[];
  push: (message: string, variant: ToastVariant, durationMs?: number) => void;
  dismiss: (id: number) => void;
}

let _nextId = 1;

export const useToastStore = create<ToastState>((set, get) => ({
  items: [],
  push: (message, variant, durationMs = 4000) => {
    const id = _nextId++;
    set((s) => ({ items: [...s.items, { id, message, variant, durationMs }] }));
    if (durationMs > 0) {
      setTimeout(() => get().dismiss(id), durationMs);
    }
  },
  dismiss: (id) => set((s) => ({ items: s.items.filter((t) => t.id !== id) })),
}));

// Convenience facade so callers don't have to subscribe to the store
// when they just want to fire and forget.
export const toast = {
  success: (message: string, durationMs?: number) =>
    useToastStore.getState().push(message, "success", durationMs),
  error: (message: string, durationMs?: number) =>
    useToastStore.getState().push(message, "error", durationMs),
  info: (message: string, durationMs?: number) =>
    useToastStore.getState().push(message, "info", durationMs),
};
